import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const ConfirmCtx = createContext(null);

const DEFAULTS = {
  title: "¿Confirmar?",
  description: "",
  confirmText: "Confirmar",
  cancelText: "Cancelar",
  destructive: false,
};

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({ ...DEFAULTS, ...options });
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const finish = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => { if (!v) finish(false); }}>
        <AlertDialogContent className="rounded-2xl" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-extrabold tracking-tight">
              {opts.title}
            </AlertDialogTitle>
            {opts.description && (
              <AlertDialogDescription className="text-sm text-neutral-600 leading-relaxed">
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              onClick={() => finish(false)}
              className="h-11 rounded-xl"
              data-testid="confirm-cancel-btn"
            >
              {opts.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => finish(true)}
              className={`h-11 rounded-xl ${
                opts.destructive
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
              }`}
              data-testid="confirm-accept-btn"
            >
              {opts.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
